import { Box, Paper, Stack, Typography } from "@mui/material"
import { FC } from "react"
import { Locker } from "shared/locker"
import { Reservation } from "shared/reservation"
import type { LockerCell, LockerGrid, SstDoor } from "shared/sst"
import type { Sst2Door, Sst2TemplateAssociation } from "shared/sst2"
import tabletIcon from "../../assets/tablet.svg"
import { OsLockerDoor } from "../OsLockerDoor"
import { OsLockerDoorV2 } from "../OsLockerDoorV2"

/**
 * Module shells and door placement for the SA read-only dashboard.
 *
 * Split out of `SaLockerDashboardView` purely for size — the geometry is a
 * faithful port of the operator dashboards' absolute-positioning maths and is
 * only meaningful next to them.
 */

// Same ratio the OS dashboards use, so module shells are recognisably identical.
export const lockerDimRatio = 0.6

export const ModuleShell: FC<{
  width: number
  height: number
  label: string
  children: React.ReactNode
}> = ({ width, height, label, children }) => (
  <Box
    sx={{
      width: `${width}px`,
      height: `${height}px`,
      px: "5px",
      position: "relative",
      display: "inline-block",
      flexShrink: 0,
    }}
  >
    <Paper
      variant="outlined"
      sx={{
        p: 0,
        pt: 3,
        bgcolor: "grey.100",
        position: "relative",
        height: "100%",
        borderRadius: 10,
        borderWidth: "5px",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="center" p={2}>
        <Typography variant="body1" fontWeight="bold">
          {label}
        </Typography>
      </Stack>
      <Box sx={{ display: "flex", position: "relative", width: "100%", height: "100%" }}>
        {children}
      </Box>
    </Paper>
  </Box>
)

const CellBox: FC<{
  cell: { x?: number; y?: number; width?: number; height?: number }
  testId?: string
  children: React.ReactNode
}> = ({ cell, testId, children }) => (
  <Box
    data-testid={testId}
    sx={{
      position: "absolute",
      top: `${(cell.y ?? 0) * lockerDimRatio}px`,
      left: `${(cell.x ?? 0) * lockerDimRatio}px`,
      width: `${(cell.width ?? 300) * lockerDimRatio}px`,
      height: `${(cell.height ?? 300) * lockerDimRatio}px`,
    }}
  >
    {children}
  </Box>
)

const PosCell: FC<{ cell: { x: number; y: number; width: number; height: number } }> = ({
  cell,
}) => (
  <CellBox cell={cell}>
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
      <img
        src={tabletIcon}
        alt="payment terminal"
        width={`${cell.width * lockerDimRatio * 0.8}px`}
        height="auto"
        style={{ transform: cell.width > cell.height ? "none" : "rotate(90deg)" }}
      />
    </Box>
  </CellBox>
)

// ── V2 ────────────────────────────────────────────────────

export const V2Module: FC<{
  template: Sst2TemplateAssociation
  doors: Sst2Door[]
  label: string
  onDoorClick: (door: Sst2Door) => void
}> = ({ template, doors, label, onDoorClick }) => {
  const cells = template.template?.cells || []
  const width = template.template?.width ?? 400
  const height = template.template?.height ?? 600

  return (
    <ModuleShell
      width={lockerDimRatio * width + 40}
      height={lockerDimRatio * height + 40}
      label={label}
    >
      {cells
        .filter((c) => c.cell_type.toUpperCase() === "DOOR")
        .map((cell) => {
          const door = doors.find((d) => d.cell_id === cell.id)
          if (!door) return null
          return (
            <CellBox key={door.id} cell={cell} testId={`sa-door-${door.id}`}>
              {/* Always clickable — including doors the operator dashboard
                  makes inert when unlinked. An admin diagnosing a desync needs
                  the unlinked door MOST. */}
              <OsLockerDoorV2
                door={door}
                cellSize={cell.size}
                alwaysClickable
                onClick={() => onDoorClick(door)}
              />
            </CellBox>
          )
        })}
      {cells
        .filter((c) => c.cell_type.toUpperCase() === "POS" || c.cell_type === "screen")
        .map((cell) => (
          <PosCell key={`pos-${cell.id}`} cell={cell} />
        ))}
    </ModuleShell>
  )
}

// ── V1 ────────────────────────────────────────────────────

export const V1Module: FC<{
  grid: LockerGrid
  locker: Locker
  doors: SstDoor[]
  label: string
  reservationFor: (door: SstDoor) => Reservation | undefined
  onDoorClick: (door: SstDoor) => void
}> = ({ grid, locker, doors, label, reservationFor, onDoorClick }) => {
  const cellByDoorId = new Map<string, LockerCell>()
  ;(grid.cells_group || []).forEach((cell: any) => {
    if (cell?.door_id) cellByDoorId.set(cell.door_id, cell)
  })

  return (
    <ModuleShell
      width={lockerDimRatio * (grid.width || 400) + 40}
      height={lockerDimRatio * (grid.height || 600) + 40}
      label={label}
    >
      {doors.map((door, j) => {
        const cell = cellByDoorId.get(door.door_id) as any
        return (
          <Box
            key={door.door_id}
            data-testid={`sa-door-${door.door_id}`}
            sx={{
              position: "absolute",
              // Same fallback flow the OS dashboard uses when a door has no cell.
              top: cell?.y === undefined ? 10 + Math.floor(j / 5) * 200 : `${cell.y * lockerDimRatio}px`,
              left: cell?.x === undefined ? 10 + (j % 5) * 200 : `${cell.x * lockerDimRatio}px`,
              width: `${lockerDimRatio * (cell?.width || 300)}px`,
              height: `${lockerDimRatio * (cell?.height || 300)}px`,
            }}
          >
            <OsLockerDoor
              cell={cell}
              tenantId={locker.tenantId}
              locker={locker}
              door={door}
              // The matched SL2 Reservation so the tile renders the state an
              // operator sees; it falls back to `door.links` when absent.
              reservation={reservationFor(door) as any}
              // "Busy" (SST holds a reservation SL2 has no Thing for) and
              // "unavailable" are inert on the operator dashboard. Here they are
              // the whole point of clicking, so every state opens the inspector.
              alwaysClickable
              onClick={() => onDoorClick(door)}
            />
          </Box>
        )
      })}
    </ModuleShell>
  )
}

